import { createSlice } from '@reduxjs/toolkit';
import { parseISO } from 'date-fns';
import { exportProfileData,
    getPrescriptionPerformance,
    getPrescriptionPerformanceByYear,
    getProfileData } from '../thunks/prescriptionThunks';
import { getYears, extractUniqueEngagements } from '../../utility';

const initialState = {
    isLoading: true,
    isModalDataLoading: true,
    isExportDataLoading: true,
    isMonthlyPrescriptionDataLoading: false,
    selectedNpiId: null,
    error: null,
    performance: null,
    profile: null,
    engagements: [],
    fieldEngagement: null,
    channels: [],
    vendors: [],
    selectedYear: new Date().getFullYear(),
    years: getYears(),
    exportProfilesData: {}
};

const prescription = createSlice({
    name: 'prescription',
    initialState,
    reducers: {
        setSelectedYear: (state, action) => {
            state.selectedYear = action.payload;
        },
        setSelectedNPI: (state, action) => {
            state.selectedNpiId = action.payload;
        },
        reset: (state) => ({
            ...initialState,
            exportProfilesData: state.exportProfilesData,
            selectedYear: new Date().getFullYear(),
            years: getYears(),
            performance: null,
            isMonthlyPrescriptionDataLoading: false
        })
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPrescriptionPerformance.fulfilled, (state, action) => {
                state.isLoading = false;
                state.performance = action.payload.prescriptionPerformance;
                state.error = null;
            })
            .addCase(getPrescriptionPerformance.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getPrescriptionPerformance.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(getPrescriptionPerformanceByYear.fulfilled, (state, action) => {
                state.isMonthlyPrescriptionDataLoading = false;
                state.performance = action.payload.prescriptionPerformance;
                state.error = null;
            })
            .addCase(getPrescriptionPerformanceByYear.pending, (state, action) => {
                state.isMonthlyPrescriptionDataLoading = true;
                state.error = null;
            })
            .addCase(getPrescriptionPerformanceByYear.rejected, (state, action) => {
                state.isMonthlyPrescriptionDataLoading = false;
                state.error = action.payload;
            })
            .addCase(getProfileData.fulfilled, (state, action) => {
                state.isModalDataLoading = false;
                const selectedNpiIKey = Object.keys(action.payload.result)[0];
                const selectedNpiIdData = action.payload.result[selectedNpiIKey];
                const profileData = Object.keys(selectedNpiIdData).includes('data') ? selectedNpiIdData.data : selectedNpiIdData;
                const activityData = profileData.activity_data[selectedNpiIKey] || [];
                state.profile = profileData;
                const engagementTableData = Object.keys(selectedNpiIdData).includes('data') ? selectedNpiIdData.data.engagement_data : selectedNpiIdData?.engagement_data;
                state.engagements =[...engagementTableData].sort((a,b) => (new Date(b.action_date) - new Date(a.action_date)));
                if (
                    selectedNpiIdData?.extended_engagement_data ||
                    selectedNpiIdData?.data?.extended_engagement_data
                ) {
                    const extendedEngagementData =
                      selectedNpiIdData?.data?.extended_engagement_data ??
                      selectedNpiIdData?.extended_engagement_data;
                  
                    const fieldEngagementData = extendedEngagementData?.[0]?.field_engagement_data ?? [];
                  
                    // Make sure it's an array with valid entries
                    if (Array.isArray(fieldEngagementData) && fieldEngagementData.length > 0) {
                        const isAllEmpty = fieldEngagementData.every(entry => !entry || Object.keys(entry).length === 0);
                        if (!isAllEmpty) {
                            const detailsData =
                          selectedNpiIdData?.data?.detailed_stats ?? selectedNpiIdData?.detailed_stats;
                            const details = detailsData?.[selectedNpiIKey];
                  
                            // Create an array to collect field engagements
                            let fieldEngagementObj = [];
                  
                            if (Array.isArray(details) && details.length > 0) {
                                fieldEngagementObj = details.map(item => ({
                                    action: item.name,
                                    action_date: item.timestamp,
                                    creative: '',
                                    engagement_type: 'Veeva Sale Rep',
                                    engagement_channel: 'Sale Rep',
                                    engagement_vendor: 'Veeva'
                                }));
                  
                                state.engagements.push(...fieldEngagementObj);
                            }
                  
                            // Add primary field engagement if available
                            const entry = fieldEngagementData[0];
                            if (entry && entry.date) {
                                const action =
                            entry.dismissed_vod_c === true
                                ? 'Dismissed'
                                : entry.marked_as_complete_vod_c === true
                                    ? 'Completed'
                                    : 'Actioned';
                  
                                const singleEntry = {
                                    action,
                                    action_date: entry.date,
                                    creative: '',
                                    engagement_type: 'Veeva Sale Rep',
                                    engagement_channel: 'Sale Rep',
                                    engagement_vendor: 'Veeva'
                                };
                                state.fieldEngagement = singleEntry;
                                state.engagements.push(singleEntry);
                            }
                        }
                    }
                }
                  
                // if(activityData.length){
                //     let completedObj;
                //         completedObj = {
                //             'action': 
                //             'action_date': fieldEngagementData[0].date,
                //             'creative': '',
                //             'engagement_type': 'Veeva Sale Rep',
                //             'engagement_channel': 'Sale Rep',
                //             'engagement_vendor': 'Veeva'
                //         }; 
                //         state.fieldEngagement = completedObj;
                //         state.engagements.push(completedObj); 
                // }
                const { channels, vendors } = extractUniqueEngagements(state.engagements);
                state.channels = channels.slice(0, 3);
                state.vendors = vendors.slice(0, 3);
                state.error = null;
            })
            .addCase(getProfileData.pending, (state, action) => {
                state.isModalDataLoading = true;
                state.error = null;
            })
            .addCase(getProfileData.rejected, (state, action) => {
                state.isModalDataLoading = false;
                state.error = action.payload;
            })
            .addCase(exportProfileData.fulfilled, (state, action) => {
                state.exportProfilesData = action.payload.result;
                state.isExportDataLoading = false;
                state.error = null;
            })
            .addCase(exportProfileData.pending, (state, action) => {
                state.isExportDataLoading = true;
                state.error = null;
            })
            .addCase(exportProfileData.rejected, (state, action) => {
                state.isExportDataLoading = false;
                state.error = action.payload;
            });
    }
});

export const { actions } = prescription;
export default prescription.reducer;
